"use client"

import { compareCategoriesByRank } from "@/lib/util/category-helpers"
import { clx } from "@medusajs/ui"
import { usePathname } from "next/navigation"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import CategoryDropdown from "../category-dropdown"
import { MoreCategoriesDropdown } from "./more-categories-dropdown"
import type { CategoryNavItem as CategoryWithStrapi } from "./index"

/** Max categories shown as primary links in the floating nav; rest go under "Kategorije". */
const FLOATING_NAV_MAX_VISIBLE = 4

interface CategoryNavClientProps {
  categories: CategoryWithStrapi[]
  /** When true, show only first N categories and put the rest in "Kategorije" dropdown. */
  compact?: boolean
  variant?: "default" | "under-row"
  /** Live sale label ("Akcija −40 %"). Set only while there is enough on /sale to promote. */
  saleLabel?: string
}

export default function CategoryNavClient({
  categories,
  compact = false,
  variant = "default",
  saleLabel,
}: CategoryNavClientProps) {
  const pathname = usePathname()
  const isSalePage = pathname.includes("/sale")

  const mainCategories = categories
    .filter((category) => !category.parent_category_id)
    .sort(compareCategoriesByRank)

  const visibleCategories = compact
    ? mainCategories.slice(0, FLOATING_NAV_MAX_VISIBLE)
    : mainCategories
  const moreCategories = compact
    ? mainCategories.slice(FLOATING_NAV_MAX_VISIBLE)
    : []

  const isUnderRow = variant === "under-row"

  return (
    <ul
      className={
        isUnderRow
          ? "flex items-center gap-x-5 whitespace-nowrap"
          : "flex items-center space-x-2"
      }
    >
      {/* While the sale is live it leads the row as a red pill; otherwise a plain link closes it. */}
      {saleLabel ? (
        <li>
          <LocalizedClientLink
            href="/sale"
            aria-current={isSalePage ? "page" : undefined}
            className="inline-flex items-center whitespace-nowrap rounded-full bg-red-600 px-3 py-1 text-sm font-semibold text-white transition-colors hover:bg-red-700"
          >
            {saleLabel}
          </LocalizedClientLink>
        </li>
      ) : null}
      {visibleCategories.map((category) => (
        <li key={category.id}>
          <CategoryDropdown
            category={category}
            allCategories={categories}
            strapiData={category.strapiData}
            variant={variant}
          />
        </li>
      ))}
      {moreCategories.length > 0 && (
        <li>
          <MoreCategoriesDropdown
            categories={moreCategories}
            allCategories={categories}
            variant={variant}
          />
        </li>
      )}
      {saleLabel ? null : (
        <li>
          <LocalizedClientLink
            href="/sale"
            className={
              isUnderRow
                ? clx(
                    "px-1 py-0.5 text-sm text-cream",
                    isSalePage
                      ? "font-bold text-white"
                      : "font-normal text-cream/90 hover:text-white"
                  )
                : clx(
                    "px-2 py-1 text-sm text-navy hover:text-gold transition-colors",
                    isSalePage && "font-semibold text-gold"
                  )
            }
          >
            AKCIJA %
          </LocalizedClientLink>
        </li>
      )}
    </ul>
  )
}

